import { useEffect, useRef, useState, type RefObject } from "react";
import { Loader2, Plus } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { ApiError } from "@/lib/api";
import { createHermesApi, type HermesChat } from "@/lib/hermes-api";
import { cn } from "@/lib/utils";
import { useHermes } from "./HermesProvider";

function formatChatTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("zh-CN", {
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function chatLabel(chat: HermesChat, liveTitle?: string): string {
  const title = (liveTitle ?? chat.title)?.trim();
  if (title) return title;
  const preview = chat.preview?.trim();
  if (preview) return preview;
  return `会话 #${chat.id}`;
}

type HermesSessionListProps = {
  /** 为 false 时不拉取；Popover 打开或全宽侧栏挂载时为 true */
  enabled?: boolean;
  /** 选中/新建会话后回调（Popover 用来关闭） */
  onSessionChosen?: () => void;
  /** popover：紧凑高度；sidebar：占满侧栏剩余高度 */
  variant?: "popover" | "sidebar";
  /** Popover 打开时聚焦搜索框用 */
  searchInputRef?: RefObject<HTMLInputElement | null>;
  className?: string;
};

/** 会话列表：新建 + 搜索切换；供 Popover 与全宽左侧栏共用。 */
export function HermesSessionList({
  enabled = true,
  onSessionChosen,
  variant = "popover",
  searchInputRef,
  className,
}: HermesSessionListProps) {
  const { accountId, chatId, startNewSession, switchSession, sessionTitles } = useHermes();
  const [chats, setChats] = useState<HermesChat[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  /** 乐观选中：切换进行中只改这一项高亮，不禁用整表 */
  const [pendingId, setPendingId] = useState<number | null>(null);
  const localSearchRef = useRef<HTMLInputElement>(null);
  const inputRef = searchInputRef ?? localSearchRef;
  const prevChatIdRef = useRef<number | null>(chatId);

  // 仅侧栏/Popover 打开或账户变化时拉取；与当前 chatId 解耦——切会话绝不 refetch
  useEffect(() => {
    if (!enabled || !accountId) return;

    let cancelled = false;
    setLoadError(null);
    setLoading(true);

    void createHermesApi(accountId)
      .listChats()
      .then(({ chats: recent }) => {
        if (!cancelled) setChats(recent);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const message = error instanceof ApiError ? error.message : "加载会话失败";
        setLoadError(message);
        setChats([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, accountId]);

  // 新建会话发出首条后 chatId: null→id，后台静默补进列表（不清空、不展示加载中）
  useEffect(() => {
    const prev = prevChatIdRef.current;
    prevChatIdRef.current = chatId;
    if (!enabled || !accountId) return;
    if (prev != null || chatId == null) return;

    let cancelled = false;
    void createHermesApi(accountId)
      .listChats()
      .then(({ chats: recent }) => {
        if (!cancelled) setChats(recent);
      })
      .catch(() => {
        // 静默失败：列表仍可用，下次打开再拉取
      });

    return () => {
      cancelled = true;
    };
  }, [chatId, enabled, accountId]);

  async function handleSelect(targetId: number) {
    // 选中源：pendingId ?? chatId；切换中禁止连点其它项（不因 isSending 禁用整表）
    if (targetId === (pendingId ?? chatId) || pendingId !== null) return;
    setPendingId(targetId);
    try {
      await switchSession(targetId);
      onSessionChosen?.();
    } finally {
      setPendingId(null);
    }
  }

  function handleNewSession() {
    // 流式中也可新建：Provider 内会 cancel 当前流
    setPendingId(null);
    startNewSession();
    onSessionChosen?.();
  }

  // 单一选中源：切换中用乐观 pending，否则用真实 chatId（避免 A/B 双亮再闪回）
  const selectedId = pendingId ?? chatId;
  const isSidebar = variant === "sidebar";

  return (
    <div
      className={cn("flex min-h-0 flex-col", isSidebar && "h-full", className)}
      aria-label="会话列表"
    >
      <div className="shrink-0 border-b border-border/60 p-1">
        <button
          type="button"
          className={cn(
            "flex w-full cursor-pointer items-center gap-2 rounded-2xl px-3 py-2 text-sm font-medium outline-hidden",
            "hover:bg-muted focus-visible:bg-muted",
            "disabled:pointer-events-none disabled:opacity-50",
          )}
          disabled={!accountId}
          onClick={handleNewSession}
        >
          <Plus className="size-4 shrink-0" />
          新对话
        </button>
      </div>

      {/* disablePointerSelection：避免 pointer 写入 data-selected；移出后 cmdk 仍会粘住该属性 */}
      <Command
        disablePointerSelection
        className={cn(
          "group/session-cmd",
          isSidebar && "flex min-h-0 flex-1 flex-col overflow-hidden",
        )}
      >
        <CommandInput ref={inputRef} placeholder="搜索会话…" />
        <CommandList
          className={cn(isSidebar ? "max-h-none min-h-0 flex-1 overflow-y-auto" : "max-h-56")}
        >
          {loading ? (
            <div className="px-3 py-6 text-center text-xs text-muted-foreground">加载中…</div>
          ) : loadError ? (
            <div className="px-3 py-6 text-center text-xs text-destructive">{loadError}</div>
          ) : chats.length === 0 ? (
            <div className="px-3 py-6 text-center text-xs text-muted-foreground">暂无历史会话</div>
          ) : (
            <>
              <CommandEmpty>没有匹配的会话</CommandEmpty>
              <CommandGroup heading="最近会话">
                {chats.map((chat) => {
                  const active = chat.id === selectedId;
                  const rowBusy = pendingId === chat.id;
                  const label = chatLabel(chat, sessionTitles[chat.id]);
                  return (
                    <CommandItem
                      key={chat.id}
                      value={`${label} ${chat.id}`}
                      // 流式中仍可切换；仅切换进行中用单行 loading，不禁用整表
                      aria-current={active ? "true" : undefined}
                      aria-busy={rowBusy || undefined}
                      onSelect={() => {
                        void handleSelect(chat.id);
                      }}
                      className={cn(
                        // 透明左边框预留宽度，选中变色时文字不位移；隐藏 registry CheckIcon 占位
                        "items-start border-l-2 border-transparent [&>svg:last-child]:hidden",
                        // 覆盖 registry data-selected:bg-muted（含首项默认选中）；hover 用 CSS :hover，移出即消
                        "data-selected:bg-transparent hover:bg-muted",
                        // 仅本 Command 内聚焦时恢复键盘 data-selected 高亮（避免抽屉聊天框焦点误触发）
                        "group-focus-within/session-cmd:data-selected:bg-muted",
                        // 会话真选中：左边条 + muted（不是 hover / 不是 cmdk data-selected）
                        active && "border-l-primary bg-muted data-selected:bg-muted",
                      )}
                    >
                      <div className="flex min-w-0 flex-1 flex-col">
                        <p className="truncate leading-snug">{label}</p>
                        <p className="mt-0.5 text-[11px] font-normal tabular-nums text-muted-foreground">
                          {formatChatTime(chat.updated_at)}
                        </p>
                      </div>
                      {rowBusy ? (
                        <Loader2
                          className="mt-0.5 size-3.5 shrink-0 animate-spin text-muted-foreground"
                          aria-hidden
                        />
                      ) : null}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </>
          )}
        </CommandList>
      </Command>
    </div>
  );
}
